import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

import {MONTHS} from "../constants"


const EcommerceSalesChart = ({ orders = [] }) => {
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  /**
   * Controls whether the best-selling products popup is visible.
   */
  const [showTopProductsModal, setShowTopProductsModal] = useState(false);


  /**
   * Returns the currently selected month label.
   */
  const selectedMonthLabel =
    MONTHS.find((month) => month.value === selectedMonth)?.label || "";

  /**
   * Builds the list of available years from WooCommerce orders.
   */
  const availableYears = useMemo(() => {
    const years = orders
      .map((order) => new Date(order.date_created).getFullYear())
      .filter((year) => !isNaN(year));

    return [...new Set(years)].sort((a, b) => b - a);
  }, [orders]);

  /**
   * Filters orders by status, selected year and selected month.
   * Only completed and on-hold orders are included in the sales overview.
   */
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const allowedStatus = ["completed", "on-hold"].includes(order.status);

      if (!allowedStatus) return false;

      const date = new Date(order.date_created);
      if (isNaN(date)) return false;

      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, "0");

      const matchYear = selectedYear ? year === selectedYear : true;
      const matchMonth = selectedMonth ? month === selectedMonth : true;

      return matchYear && matchMonth;
    });
  }, [orders, selectedYear, selectedMonth]);

  /**
   * Calculates the revenue based on the selected year/month filters.
   */
  const filteredRevenue = useMemo(() => {
    return filteredOrders.reduce(
      (total, order) => total + Number(order.total || 0),
      0,
    );
  }, [filteredOrders]);

  //  * Changes the revenue label depending on the selected filter.

  const productsLabel =
    selectedYear && selectedMonth
      ? `Products • ${MONTHS.find((m) => m.value === selectedMonth)?.label} ${selectedYear}`
      : selectedYear
        ? `Products • ${selectedYear}`
        : "Products • All Time";

  /**
   * Changes the revenue label depending on the selected filter.
   */
  const revenueLabel = selectedMonth
    ? "Month Revenue"
    : selectedYear
      ? "Year Revenue"
      : "Total Revenue";

  /**




   * Generates a subtitle describing the current filters.
   */
  const filterDescription = (() => {
    if (selectedYear && selectedMonth) {
      return `${selectedMonthLabel} ${selectedYear}`;
    }

    if (selectedYear) {
      return `Year ${selectedYear}`;
    }

    if (selectedMonth) {
      return selectedMonthLabel;
    }

    return "All Years";
  })();

  /**
   * Groups filtered orders by month or by day.
   * If a month is selected, the chart shows daily order counts.
   * Otherwise, it shows monthly order counts.
   */
  const { categories, seriesData, orderNumbersByPeriod } = useMemo(() => {
    // Stores the number of orders and their order numbers for each period.
    const grouped = {};

    filteredOrders.forEach((order) => {
      const date = new Date(order.date_created);

      // Ignore orders with an invalid creation date.
      if (isNaN(date)) return;

      /**
       * Creates the grouping key.
       *
       * Example when a month is selected:
       * "2026-07-10"
       *
       * Example when no month is selected:
       * "2026-07"
       */
      const key = selectedMonth
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0",
          )}-${String(date.getDate()).padStart(2, "0")}`
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0",
          )}`;

      // Creates the initial structure for a period.
      if (!grouped[key]) {
        grouped[key] = {
          count: 0,
          orderNumbers: [],
        };
      }

      // Increases the number of orders for this period.
      grouped[key].count += 1;

      // Stores the visible WooCommerce order number.
      // The order ID is used as a fallback when the number is unavailable.
      grouped[key].orderNumbers.push(order.number || order.id || "Unknown");
    });

    // Sorts the periods chronologically.
    const sortedKeys = Object.keys(grouped).sort();

    // Creates the labels displayed on the chart's X axis.
    const categories = sortedKeys.map((key) => {
      // When a month is selected, display only the day.
      if (selectedMonth) {
        const [, , day] = key.split("-");
        return day;
      }

      // Otherwise, display the abbreviated month and year.
      const [year, month] = key.split("-");

      return new Date(Number(year), Number(month) - 1).toLocaleString(
        "default",
        {
          month: "short",
          year: "numeric",
        },
      );
    });

    // Creates the numeric values used by the chart series.
    const seriesData = sortedKeys.map((key) => grouped[key].count);

    // Creates one order-number list for each chart point.
    const orderNumbersByPeriod = sortedKeys.map(
      (key) => grouped[key].orderNumbers,
    );

    return {
      categories,
      seriesData,
      orderNumbersByPeriod,
    };
  }, [filteredOrders, selectedMonth]);

  /**
   * ApexCharts configuration.
   */
  /**
   * ApexCharts configuration.
   */
  const chartOptions = {
    chart: {
      type: "area",
      height: 360,
      zoom: { enabled: false },
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },

    dataLabels: {
      enabled: true,
      offsetY: -10,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        colors: ["#222731"],
      },
      background: {
        enabled: false,
      },
    },

    /**
     * Keeps the chart line smooth with the original width.
     */
    stroke: {
      curve: "smooth",
      width: 2,
    },

    /**
     * Keeps the original chart point style.
     */
    markers: {
      size: 6,
      hover: {
        size: 8,
      },
      strokeWidth: 2,
      colors: ["#fafaf9"],
      strokeColors: ["#222731"],
    },

    /**
     * Keeps the original chart grid style.
     */
    grid: {
      borderColor: "#e7e5e4",
      strokeDashArray: 4,
    },

    xaxis: {
      categories,
      labels: {
        rotate: -45,
        style: {
          colors: "#78716c",
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },

    yaxis: {
      min: 0,
      title: {
        text: "Orders",
        style: {
          color: "#a8a29e",
        },
      },
      labels: {
        style: {
          colors: "#78716c",
        },
      },
    },

    /**
     * Displays the order count and WooCommerce order numbers
     * when the user hovers over a chart point.
     */
    tooltip: {
      shared: false,
      intersect: true,

      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        // Gets the label of the hovered chart point.
        const period = w.globals.labels[dataPointIndex] || "";

        // Gets the total number of orders for this period.
        const count = series[seriesIndex][dataPointIndex] || 0;

        // Gets the order numbers associated with this chart point.
        const orderNumbers = orderNumbersByPeriod[dataPointIndex] || [];

        /**
 * Builds the HTML list of WooCommerce order numbers.
 */
const orderList = orderNumbers
  .map(
    (orderNumber) => `
      <li style="
        margin: 4px 0;
      ">
        Order #${orderNumber}
      </li>
    `,
  )
  .join("");

        return `
        <div style="
          padding: 12px;
          max-width: 260px;
          font-family: Inter, sans-serif;
        ">
          <div style="
            margin-bottom: 6px;
            font-weight: 700;
            color: #44403c;
          ">
            ${period}
          </div>

          <div style="
            margin-bottom: 8px;
            font-size: 13px;
            color: #78716c;
          ">
            <strong>
              ${count} ${count === 1 ? "order" : "orders"}
            </strong>
          </div>


          <ul style="
  margin: 0;
  padding: 0;
  list-style-position: inside;
  color: #57534e;
  font-size: 13px;
">
  ${orderList}
</ul>
      `;
      },
    },

    /**
     * Keeps the original gradient area below the line.
     */
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: ["#48CFCB"],
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0.06,
        stops: [0, 80, 100],
      },
    },

    /**
     * Keeps the original main chart color.
     */
    colors: ["#222731"],
  };

  /**
   * Chart series data.
   */
  const series = [
    {
      name: "Orders",
      data: seriesData,
    },
  ];

  /**
   * Builds a ranking of the best-selling products.
   * The ranking is based on the currently filtered orders.
   */
  const topProducts = useMemo(() => {
    const products = {};

    filteredOrders
      .filter((order) => order.status !== "cancelled")
      .forEach((order) => {
        (order.line_items || []).forEach((item) => {
          const productName = item.name || "Unknown product";

          if (!products[productName]) {
            products[productName] = {
              name: productName,
              quantity: 0,
              total: 0,
            };
          }

          products[productName].quantity += Number(item.quantity || 0);
          products[productName].total += Number(item.total || 0);
        });
      });

    return Object.values(products).sort((a, b) => b.quantity - a.quantity);
    // .slice(0, 10);
  }, [filteredOrders]);

  // Body Box
  return (
    <div className="rounded-2xl border border-stone-100 bg-stone-50 h-[680px] p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-800">
            Sales Overview
          </h3>

          <p className="text-sm text-stone-500">
            {filteredOrders.length} total orders
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => {
              const year = e.target.value;

              setSelectedYear(year);

              if (!year) {
                setSelectedMonth("");
              }
            }}
            className="rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            <option value="">Select Year</option>

            {availableYears.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            disabled={!selectedYear}
            className="rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {MONTHS.map((month) => (
              <option key={month.value || "all"} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {seriesData.length === 0 ? (
        <div className="flex h-56 items-center justify-center text-stone-400">
          No order data available
        </div>
      ) : (
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="area"
          height={360}
        />
      )}

      {/* Sales overview footer */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="w-80 rounded-xl border border-stone-200 bg-white px-6 py-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
            {revenueLabel}
          </p>

          <p className="mt-2 text-3xl font-bold text-stone-800">
            {filteredRevenue.toLocaleString("en-US", {
              style: "currency",
              currency: filteredOrders[0]?.currency || "EUR",
            })}
          </p>
        </div>

        {/* Product overview button */}
        <button
          type="button"
          onClick={() => setShowTopProductsModal(true)}
          className="w-80 cursor-pointer rounded-xl border border-stone-800 bg-white px-6 py-4 text-left shadow-sm hover:bg-stone-50"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
            {productsLabel}
          </p>

          <p className="mt-2 text-xl font-bold text-stone-800">
            Product Overview
          </p>
        </button>
      </div>

      {/* Best selling products popup */}
      {showTopProductsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-stone-800">
                  Product Overview
                </h3>

                <p className="text-sm text-stone-500">{filterDescription}</p>
              </div>

              <button
                type="button"
                onClick={() => setShowTopProductsModal(false)}
                className="rounded-lg bg-stone-100 px-3 border py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-200"
              >
                Close
              </button>
            </div>
            {/* Summary cards */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
                  Total Products
                </p>

                <p className="mt-2 text-2xl font-bold text-stone-800">
                  {topProducts.reduce(
                    (total, product) => total + product.quantity,
                    0,
                  )}
                </p>
              </div>

              <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
                  Total Revenue
                </p>

                <p className="mt-2 text-2xl font-bold text-stone-800">
                  {topProducts
                    .reduce((total, product) => total + product.total, 0)
                    .toLocaleString("en-US", {
                      style: "currency",
                      currency: filteredOrders[0]?.currency || "EUR",
                    })}
                </p>
              </div>
            </div>
            {topProducts.length === 0 ? (
              <p className="text-sm text-stone-400">
                No product data available
              </p>
            ) : (
              <div className="max-h-[55vh] overflow-y-auto overflow-x-auto rounded-xl border border-stone-100">
                <table className="w-full text-left text-sm">
                  <thead className="bg-stone-50 text-stone-500">
                    <tr>
                      <th className="p-3 font-medium">Product</th>
                      <th className="p-3 text-center font-medium">Quantity</th>
                      <th className="p-3 text-right font-medium">Revenue</th>
                    </tr>
                  </thead>

                  <tbody>
                    {topProducts.map((product) => (
                      <tr
                        key={product.name}
                        className="border-t border-stone-100"
                      >
                        <td className="p-3 text-stone-700">{product.name}</td>

                        <td className="p-3 text-center font-medium text-stone-700">
                          {product.quantity}
                        </td>

                        <td className="p-3 text-right font-medium text-stone-700">
                          {product.total.toLocaleString("en-US", {
                            style: "currency",
                            currency: filteredOrders[0]?.currency || "EUR",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EcommerceSalesChart;
